/**
 * User model based on API response structure
 */

/**
 * @typedef {Object} Language
 * @property {string} id - Language ID
 * @property {string} name - Language name (e.g., "French", "Spanish", "English")
 * @property {string} code - Language code (e.g., "fr", "es", "en")
 * @property {string|null} deletedAt - Soft delete timestamp
 * @property {string} createdAt - Creation date in ISO format
 * @property {string} updatedAt - Last update date in ISO format
 */

/**
 * @typedef {Object} Role
 * @property {string} id - Role ID
 * @property {string} role - Role name (e.g., "admin", "user")
 */

/**
 * @typedef {Object} ApiUser
 * @property {string} id - User ID
 * @property {string} firstName - User's first name
 * @property {string} lastName - User's last name
 * @property {string} culturalLanguage - User's cultural language
 * @property {Language} commonLanguage - User's common language object
 * @property {string} email - User's email address
 * @property {Role} role - User's role object
 * @property {string} createdAt - Creation date in ISO format
 * @property {string} updatedAt - Last update date in ISO format
 * @property {string|null} deletedAt - Soft delete timestamp
 */

/**
 * Transform API user data to internal format
 * @param {ApiUser} apiUser - User data from API
 * @returns {Object} Transformed user object for internal use
 */
export function transformUser(apiUser) {
  if (!apiUser || typeof apiUser !== 'object') {
    throw new Error('Invalid user data provided to transformUser');
  }

  return {
    id: apiUser.id || '',
    firstName: apiUser.firstName || '',
    lastName: apiUser.lastName || '',
    name: apiUser.lastName || '', // For backward compatibility with existing UI
    email: apiUser.email || '',
    role: apiUser.role?.role || 'user',
    roleId: apiUser.role?.id || null,
    culturalLanguage: apiUser.culturalLanguage || '',
    cultureLanguage: apiUser.culturalLanguage || '', // For backward compatibility
    commonLanguage: apiUser.commonLanguage?.code || 'fr',
    commonLanguageId: apiUser.commonLanguage?.id || null,
    commonLanguageName: apiUser.commonLanguage?.name || 'French',
    createdAt: apiUser.createdAt || new Date().toISOString(),
    updatedAt: apiUser.updatedAt || new Date().toISOString(),
    deletedAt: apiUser.deletedAt || null,
    // Keep original API data for reference
    _original: apiUser
  };
}

/**
 * Get user display name (firstName lastName)
 * @param {Object} user - Transformed user object
 * @returns {string} Display name
 */
export function getUserDisplayName(user) {
  if (!user) return '';
  
  const firstName = user.firstName || '';
  const lastName = user.lastName || user.name || '';
  
  return [firstName, lastName].filter(Boolean).join(' ');
}

/**
 * Get user's full name (lastName, firstName)
 * @param {Object} user - Transformed user object
 * @returns {string} Full name in "Last, First" format
 */
export function getUserFullName(user) {
  if (!user) return '';
  
  const firstName = user.firstName || '';
  const lastName = user.lastName || user.name || '';
  
  if (firstName && lastName) {
    return `${lastName}, ${firstName}`;
  }
  
  return lastName || firstName || '';
}

/**
 * Check if user is admin
 * @param {Object} user - Transformed user object
 * @returns {boolean} True if user is admin
 */
export function isUserAdmin(user) {
  return user?.role === 'admin';
}

/**
 * Check if user is deleted
 * @param {Object} user - Transformed user object
 * @returns {boolean} True if user is soft deleted
 */
export function isUserDeleted(user) {
  return !!user?.deletedAt;
}
